import * as Sentry from '@sentry/react';
import { AlertTriangle } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface SectionErrorProps {
  error?: Error;
  resetError?: () => void;
  label?: string;
}

function SectionErrorFallback({ error, resetError, label }: SectionErrorProps) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[30vh] gap-4 p-8 text-center">
      <AlertTriangle className="w-10 h-10 text-amber-500" />
      <div>
        <p className="font-semibold text-gray-800">{label ?? 'This section'} failed to load.</p>
        <p className="text-sm text-gray-500 mt-1">
          {error?.message ?? 'An unexpected error occurred.'}
        </p>
      </div>
      {resetError && (
        <Button variant="outline" size="sm" onClick={resetError}>
          Try again
        </Button>
      )}
    </div>
  );
}

export function SectionBoundary({
  children,
  label,
}: {
  children: React.ReactNode;
  label?: string;
}) {
  return (
    <Sentry.ErrorBoundary
      fallback={({ error, resetError }) => (
        <SectionErrorFallback
          error={error instanceof Error ? error : undefined}
          resetError={resetError}
          label={label}
        />
      )}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
}
